import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
	ApplicationConfig,
	EnvironmentProviders,
	inject,
	provideAppInitializer,
	Provider,
	provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	PreloadAllModules,
	provideRouter,
	withPreloading,
} from '@angular/router';
import { AlertService } from '@context/frontend/alert';
import {
	API_URL,
	APP_VERSION,
	ORGANIZATION_ID,
	provideContextTaiga,
	provideFirebase,
	SUPPORT_EMAIL,
	USER,
} from '@context/frontend/common';
import { superAuthBearerInterceptor } from '@context/frontend/user';
import { version } from '@context/package';
import { User } from '@context/shared/types/user';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import {
	tuiDateFormatProvider,
	tuiScrollbarOptionsProvider,
} from '@taiga-ui/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

type Providers = (Provider | EnvironmentProviders)[];

const BOOT_TRANSLATION_TIMEOUT_MS = 8000;

const FirebaseProviders: Providers = environment.firebase
	? [provideFirebase(environment.firebase, environment.emulators)]
	: [];

export const appConfig: ApplicationConfig = {
	providers: [
		provideAnimations(),
		provideZonelessChangeDetection(),
		provideRouter(appRoutes, withPreloading(PreloadAllModules)),
		...FirebaseProviders,
		provideContextTaiga(),
		AlertService,
		tuiScrollbarOptionsProvider({ mode: 'hover' }),
		tuiDateFormatProvider({ mode: 'mm/dd/yyyy', separator: '/' }),
		provideHttpClient(withInterceptors([superAuthBearerInterceptor])),
		provideTransloco({
			config: {
				availableLangs: ['en'],
				defaultLang: 'en',
				reRenderOnLangChange: true,
				prodMode: true,
			},
			loader: TranslocoHttpLoader,
		}),
		{ provide: SUPPORT_EMAIL, useValue: environment.supportEmail },
		{ provide: API_URL, useValue: environment.apiUrl },
		{ provide: APP_VERSION, useValue: version },
		{
			provide: ORGANIZATION_ID,
			useValue: new BehaviorSubject<string | null>(null),
		},
		{ provide: USER, useValue: new BehaviorSubject<User | null>(null) },
		provideAppInitializer(() => {
			const transloco = inject(TranslocoService);
			transloco.setDefaultLang('en');
			// Don't let a stalled/blocked translation request hang bootstrap
			// forever. The load keeps running in the background and the UI
			// re-renders once it resolves.
			return Promise.race([
				firstValueFrom(transloco.selectTranslation('en')),
				new Promise((resolve) =>
					setTimeout(resolve, BOOT_TRANSLATION_TIMEOUT_MS),
				),
			]);
		}),
	],
};
